import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import surveyApi from '../surveyApi';
import { typeMeta } from '../questionTypes';
import { labelFor } from '../../i18n/strings';

const SERIES = '#f59e0b';
const GRID = '#334155';
const AXIS = { fill: '#94a3b8', fontSize: 11 };
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '');
const roundTick = (r) => `R${r.round_number}${r.label ? ` · ${r.label}` : ''}`;

// Change vs the previous round that has data. `higherIsBetter` false for avg rank,
// null when a rise is neither good nor bad (a choice option's share).
function Delta({ value, prev, digits = 2, unit = '', higherIsBetter = true }) {
  if (value == null || prev == null) return null;
  const d = Math.round((value - prev) * 10 ** digits) / 10 ** digits;
  if (d === 0) return <span className="inline-flex items-center gap-0.5 text-xs text-slate-400"><Minus className="w-3 h-3" /> no change</span>;
  const tone = higherIsBetter === null ? 'text-slate-300'
    : (higherIsBetter ? d > 0 : d < 0) ? 'text-emerald-300' : 'text-rose-300';
  const Icon = d > 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs ${tone}`}>
      <Icon className="w-3 h-3" /> {d > 0 ? '+' : ''}{d}{unit} vs previous
    </span>
  );
}

function ChartTip({ active, payload, label, suffix = '' }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-slate-800 p-2 border border-slate-700 rounded shadow-md text-xs">
      <p className="text-slate-300 font-medium">{label}</p>
      <p className="text-slate-100">{payload[0].value ?? '—'}{suffix}</p>
      {p.answered != null && <p className="text-slate-400">{p.answered} answered</p>}
    </div>
  );
}

const lastTwo = (arr, key) => {
  const withData = arr.filter((x) => x[key] != null);
  return [withData[withData.length - 1]?.[key] ?? null, withData[withData.length - 2]?.[key] ?? null];
};

/**
 * Round-over-round view. Questions are identical across rounds (answered questions
 * are locked), so a question's numbers are directly comparable round to round.
 */
export default function SurveyTrends({ surveyId, language }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const d = await surveyApi.adminTrends(surveyId, language);
        if (active) { setData(d); setError(''); }
      } catch (e) {
        if (active) setError(e.message || 'Failed to load trends');
      }
    })();
    return () => { active = false; };
  }, [surveyId, language]);

  if (error) return <div className="text-rose-300">{error}</div>;
  if (!data) return <div className="text-slate-400">Loading…</div>;

  const { rounds, questions } = data;
  const tickOf = Object.fromEntries(rounds.map((r) => [r.id, roundTick(r)]));

  if (rounds.length < 2) {
    return (
      <div className="rounded-lg bg-slate-800 border border-slate-700 p-6 text-slate-300">
        <p className="font-medium text-slate-100 mb-1">Only one round so far.</p>
        <p className="text-sm text-slate-400">
          When it's time to re-run this survey, open the survey editor and use <strong>Start new round</strong>.
          Round 1 is kept as-is, everyone can respond again, and this tab will compare the rounds side by side.
        </p>
      </div>
    );
  }

  const participation = rounds.map((r) => ({
    name: roundTick(r), completed: r.completed, rate: r.response_rate,
  }));
  const allLanguages = [...new Set(rounds.flatMap((r) => Object.keys(r.by_language || {})))];

  let qNumber = 0;
  return (
    <div className="space-y-6">
      {language && (
        <div className="text-sm text-slate-400">
          Showing only <span className="text-slate-200">{labelFor(language)}</span> responses in every round.
        </div>
      )}

      {/* Participation per round */}
      <div className="rounded-lg bg-slate-800 border border-slate-700 p-4">
        <h3 className="text-sm uppercase tracking-wide text-slate-400 mb-3">Completed responses per round</h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={participation} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="name" tick={AXIS} />
              <YAxis allowDecimals={false} tick={AXIS} />
              <Tooltip content={<ChartTip />} cursor={{ fill: '#33415555' }} />
              <Bar dataKey="completed" name="Responses" fill={SERIES} radius={[4, 4, 0, 0]} maxBarSize={56}>
                <LabelList dataKey="completed" position="top" fill="#cbd5e1" fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm">
            <thead className="text-slate-400 text-xs">
              <tr>
                <th className="text-left py-1 pr-4">Round</th>
                <th className="text-left py-1 pr-4">Dates</th>
                <th className="text-right py-1 pr-4">Responses</th>
                {!language && <th className="text-right py-1 pr-4">Response rate</th>}
                {allLanguages.length > 1 && allLanguages.map((l) => (
                  <th key={l} className="text-right py-1 pr-4">{labelFor(l)}</th>
                ))}
              </tr>
            </thead>
            <tbody className="text-slate-200">
              {rounds.map((r) => (
                <tr key={r.id} className="border-t border-slate-700/60">
                  <td className="py-1 pr-4">{roundTick(r)}</td>
                  <td className="py-1 pr-4 text-slate-400">{[fmtDate(r.opened_at), fmtDate(r.closed_at)].filter(Boolean).join(' – ') || '—'}</td>
                  <td className="py-1 pr-4 text-right">{r.completed}</td>
                  {!language && <td className="py-1 pr-4 text-right">{r.response_rate != null ? `${r.response_rate}%` : '—'}</td>}
                  {allLanguages.length > 1 && allLanguages.map((l) => (
                    <td key={l} className="py-1 pr-4 text-right">{(r.by_language || {})[l] || 0}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {questions.map((q) => {
        qNumber += 1;
        const meta = typeMeta(q.question_type);
        return (
          <div key={q.id} className="rounded-lg bg-slate-800 border border-slate-700 p-4">
            <div className="flex items-start justify-between gap-2 mb-3">
              <h3 className="text-slate-100 font-medium">
                <span className="text-slate-500 mr-1">{qNumber}.</span>{q.question_text}
              </h3>
              <span className="px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-300 shrink-0">{meta.short}</span>
            </div>
            <QuestionTrend q={q} tickOf={tickOf} />
          </div>
        );
      })}
    </div>
  );
}

function QuestionTrend({ q, tickOf }) {
  const type = q.question_type;

  // Numeric: one line of the round average (NPS: the NPS score).
  if (['likert', 'rating', 'nps'].includes(type)) {
    const key = type === 'nps' ? 'nps' : 'average';
    const points = q.per_round.map((p) => ({ name: tickOf[p.round_id], value: p[key], answered: p.answered }));
    const [latest, prev] = lastTwo(points, 'value');
    const max = type === 'nps' ? 100
      : type === 'likert' ? Math.max(...(q.config?.points || []).map((p) => p.value), 5)
      : (q.config?.scale_max ?? 5);
    const min = type === 'nps' ? -100 : (type === 'rating' ? (q.config?.scale_min ?? 1) : 1);
    return (
      <div>
        <div className="flex items-baseline gap-3 mb-2">
          <span className="text-2xl font-bold text-amber-300">{latest ?? '—'}</span>
          <span className="text-xs text-slate-400">{type === 'nps' ? 'NPS, latest round' : `avg of ${max}, latest round`}</span>
          <Delta value={latest} prev={prev} digits={type === 'nps' ? 0 : 2} />
        </div>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 18, right: 24, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="name" tick={AXIS} padding={{ left: 24, right: 24 }} />
              <YAxis domain={[min, max]} tick={AXIS} allowDecimals={type !== 'nps'} />
              <Tooltip content={<ChartTip />} cursor={{ stroke: '#64748b', strokeWidth: 1 }} />
              <Line type="linear" dataKey="value" stroke={SERIES} strokeWidth={2} connectNulls
                dot={{ r: 4, fill: SERIES, stroke: '#1e293b', strokeWidth: 2 }} activeDot={{ r: 6 }} isAnimationActive={false}>
                <LabelList dataKey="value" position="top" fill="#cbd5e1" fontSize={11} />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Choice: share of answers per option, per round (with change vs previous round).
  if (q.per_round[0]?.options && type !== 'ranking') {
    return (
      <OptionTable q={q} tickOf={tickOf} valueKey="pct" fmt={(v) => (v == null ? '—' : `${v}%`)} unit=" pts" higherIsBetter={null} />
    );
  }

  if (type === 'ranking') {
    return (
      <>
        <p className="text-xs text-slate-400 mb-2">Average position per round (lower is better).</p>
        <OptionTable q={q} tickOf={tickOf} valueKey="average_rank" fmt={(v) => (v == null ? '—' : v)} higherIsBetter={false} />
      </>
    );
  }

  // Text: comparable only by volume; read them on the Results tab per round.
  return (
    <div className="flex flex-wrap gap-4 text-sm text-slate-300">
      {q.per_round.map((p) => (
        <span key={p.round_id}><span className="text-slate-500">{tickOf[p.round_id]}:</span> {p.answered} written answers</span>
      ))}
    </div>
  );
}

function OptionTable({ q, tickOf, valueKey, fmt, unit = '', higherIsBetter }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-slate-400 text-xs">
          <tr>
            <th className="text-left py-1 pr-4">Option</th>
            {q.per_round.map((p) => <th key={p.round_id} className="text-right py-1 pr-4">{tickOf[p.round_id]}</th>)}
            <th className="text-right py-1">Change</th>
          </tr>
        </thead>
        <tbody className="text-slate-200">
          {q.options.map((o) => {
            const vals = q.per_round.map((p) => (p.options || []).find((x) => x.id === o.id)?.[valueKey] ?? null);
            const withData = q.per_round.map((p, i) => (p.answered ? vals[i] : null)).filter((v) => v != null);
            return (
              <tr key={o.id} className="border-t border-slate-700/60">
                <td className="py-1 pr-4">{o.option_text}</td>
                {vals.map((v, i) => <td key={i} className="py-1 pr-4 text-right">{q.per_round[i].answered ? fmt(v) : '—'}</td>)}
                <td className="py-1 text-right">
                  <Delta value={withData[withData.length - 1]} prev={withData[withData.length - 2]} digits={1}
                    unit={unit} higherIsBetter={higherIsBetter} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
